import NextImage, { ImageLoaderProps } from 'next/image';

import { CardButton } from '../../../components/Cards/CardButton';
import { Heading } from '../../../components/Heading/Heading';
import { getCatchCardEventProperties } from '../../../components/PostsViewer/helpers/getCatchCardEventProperties';
import { openPostViewerEventSources } from '../../../components/PostsViewer/types/openPostViewerEventSources';
import { Text } from '../../../components/Text/Text';
import { useAnalytics } from '../../../contexts/AnalyticsContext/AnalyticsContext';
import { getNodesFromConnection } from '../../../helpers/graphql';
import {
  GetSingleSpecies_entry_catchesWithImages_edges_node,
  GetSingleSpecies_entry_exampleCatchWithImage,
} from '../../../interfaces/graphql';
import styles from '../Species.module.css';
import { getImageTitle } from '../speciesHelpers';
import { getImgProxyLoader } from '../../../helpers/imageLoaders/imgProxy';

interface IProps {
  exampleCatchWithImage: GetSingleSpecies_entry_exampleCatchWithImage | null;
  displayName: string;
  recentCatches: GetSingleSpecies_entry_catchesWithImages_edges_node[];
  openPostViewer: (catchId: string, eventSource?: openPostViewerEventSources) => Promise<void>;
}

export const HeaderImages = ({
  exampleCatchWithImage,
  displayName,
  recentCatches,
  openPostViewer,
}: IProps) => {
  const { currentPage } = useAnalytics();

  const exampleImages = getNodesFromConnection(exampleCatchWithImage?.post.images);
  const exampleImage = exampleImages.length > 0 ? exampleImages[0] : undefined;
  const exampleImageUrl = exampleImage?.url ?? '';
  const exampleImageWidth = exampleImage?.width ?? 0;
  const exampleImageHeight = exampleImage?.height;

  const exampleImageLoader = ({ src, width, quality }: ImageLoaderProps): string =>
    getImgProxyLoader(src, width, exampleImageWidth, exampleImageHeight, {
      quality,
    });

  return (
    <div className={`${styles.headerImages} mb-sm md:mb-0`}>
      <div className={`${styles.imagesWrapper} md:sticky`}>
        {exampleCatchWithImage && (
          <div className="grid grid-cols-1 grid-rows-1">
            <CardButton
              className="relative col-start-1 row-start-1 w-full pb-[100%]"
              id={exampleCatchWithImage.post._id}
              key={exampleCatchWithImage.post._id}
              eventProperties={getCatchCardEventProperties({
                postId: exampleCatchWithImage.post._id,
                currentPage,
                source: 'species-top-section',
              })}
              onClick={() =>
                void openPostViewer(
                  exampleCatchWithImage.post._id,
                  openPostViewerEventSources.SpeciesHeaderImage,
                )
              }
              ariaLabel="a catch of this species"
            >
              <NextImage
                alt={`The most popular recent ${displayName} catch on Fishbrain`}
                className={`size-full object-cover transition-all ${styles.mainImage}`}
                src={exampleImageUrl}
                loader={exampleImageLoader}
                fill
                priority
                sizes="(min-width: 1680px) 638px,(min-width: 990px) 41.67vw, 100vw"
              />
            </CardButton>
            <div className="pointer-events-none relative col-start-1 row-start-1 size-full">
              <Text
                variant="sm"
                as="span"
                className="text-trench absolute bottom-0 left-0 mb-3.5 bg-white/80 px-3 py-2 sm:mb-4 md:mb-10"
              >
                {getImageTitle(exampleCatchWithImage)}
              </Text>
            </div>
          </div>
        )}
        {recentCatches.length > 0 && (
          <div data-testid="Recent catches header section">
            <Heading variant="2xs" className="mt-6 mb-2.5 md:mb-3" as="h3">
              Recently caught {displayName}
            </Heading>

            <div
              className={`grid grid-cols-4 grid-rows-1 gap-3 sm:grid-cols-6 md:grid-cols-5 md:gap-4 ${styles.miniCatchesGrid}`}
            >
              {recentCatches.map(({ post }) => {
                const image = getNodesFromConnection(post.images)[0];
                const imageUrl = image.url || '';
                const imageWidth = image.width || 0;
                const imageHeight = image.height;

                const imageLoader = ({ src, width, quality }: ImageLoaderProps): string =>
                  getImgProxyLoader(src, width, imageWidth, imageHeight, { quality });

                return (
                  <CardButton
                    className="relative col-span-1 pb-[100%] hover:shadow-md"
                    id={post._id}
                    key={post._id}
                    eventProperties={getCatchCardEventProperties({
                      postId: post._id,
                      currentPage,
                      source: 'species-top-section',
                    })}
                    onClick={() =>
                      void openPostViewer(post._id, openPostViewerEventSources.SpeciesHeaderImage)
                    }
                    ariaLabel="a recent catch of this species"
                  >
                    <NextImage
                      alt="most liked catch image"
                      className="size-full object-cover"
                      src={imageUrl}
                      loader={imageLoader}
                      fill
                      sizes="(min-width: 1680px) 115px,(min-width: 990px) 8.33vw ,(min-width: 600px) 16.67vw, 25vw"
                    />
                  </CardButton>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
